import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Box, Button, Typography, Collapse } from '@mui/material';
import { styled } from '@mui/system';
import { alpha } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RTL from '../utils/RTL';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';

const FEATURES = [
  {
    key: 'learning',
    icon: SchoolIcon,
    title: 'یادگیری سریع کار با نرم‌افزار',
    text: 'این نرم‌افزار طوری طراحی شده است که همه پرسنل آموزشگاه بتوانند به راحتی با آن تعامل داشته باشند. به طور مثال فرایند حضور و غیاب توسط دفتردار آموزشگاه به سرعت قابل انجام است و گزارش کاملی از فهرست حضور و غیاب‌های هنرجو و ثبت‌نام‌های انجام شده در دسترس قرار می‌گیرد.',
  },
  {
    key: 'manager',
    icon: SupervisorAccountIcon,
    title: 'کنترل آسان فعالیت‌های آموزشگاه توسط مدیر',
    text: 'به عنوان مدیر کافی است به فهرست همه پردازش‌های ثبت شده نگاه کنید تا متوجه شوید در آموزشگاه‌تان چه می‌گذرد! با ارزیابی‌های آماری که روز به روز در حال کامل‌تر شدن است بهتر می‌توانید سکان هدایت آموزشگاه خود را در دست گرفته و به مسیر مورد نظر هدایت کنید.',
  },
  {
    key: 'accounts',
    icon: AccountCircleIcon,
    title: 'حساب کاربری برای هنرجویان و مدرسین',
    text: 'در این نرم‌افزار مدرسین و هنرجویان حساب کاربری خواهند داشت که می‌توانند هر وقت بخواهند از وضعیت کاری یا تحصیلی خود مطلع شوند. مدرسین می‌توانند از جزئیات کارکرد خود و محاسبه حقوق و درآمد مطلع شوند و هنرجویان نیز می‌توانند جزئیات ثبت‌نام و حضور و غیاب خود را به صورت آنلاین مشاهده کنند.',
  },
  {
    key: 'notes',
    icon: EditNoteIcon,
    title: 'امکان یادداشت‌گذاری برای مدرسین',
    text: 'این امکان برای مدرسین در نظر گرفته شده است که بتوانند برای هر هنرجو یادداشت‌هایی در مورد نحوه عملکرد وی و یا تمرین‌های ارائه شده به وی در سیستم ثبت نمایند. این امکان به مدرسین کمک می‌کند تا مسیر آموزشی هنرجو را بهتر بررسی نمایند.',
  },
];

const OTHER_FEATURES = [
  'محاسبه آسان حقوق مدرسین',
  'دستیار هوشمند مدیر و گزارش روزانه',
  'ثبت یادداشت امور روزمره آموزشگاه',
  'پشتیبانی آنلاین',
];

const Page = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.default,
}));

const NavBar = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 3),
  backgroundColor: theme.palette.background.chrome,
  borderBottom: `1px solid ${theme.palette.border.main}`,
}));

const NavLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Hero = styled(Box)(({ theme }) => ({
  background: theme.palette.brand.gradient,
  color: theme.palette.primary.contrastText,
  textAlign: 'center',
  padding: theme.spacing(10, 2, 12),
}));

const Section = styled(Box)(({ theme }) => ({
  maxWidth: '64rem',
  margin: '0 auto',
  padding: theme.spacing(8, 2, 2),
}));

const CardsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(13rem, 1fr))',
  gap: theme.spacing(2),
}));

const FeatureCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  cursor: 'pointer',
  textAlign: 'center',
  padding: theme.spacing(3, 2),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.neutral,
  border: `2px solid ${active ? theme.palette.primary.main : 'transparent'}`,
  transition: 'transform 0.15s ease, border-color 0.15s ease',
  '&:hover': { transform: 'translateY(-4px)' },
}));

const FeatureText = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.table,
  lineHeight: 2,
}));

const Footer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(8),
  padding: theme.spacing(3),
  textAlign: 'center',
  backgroundColor: theme.palette.background.chrome,
  borderTop: `1px solid ${theme.palette.border.main}`,
}));

export default function HomePage() {
  const { user, isLoading } = useAuth();
  const [openFeature, setOpenFeature] = React.useState(null);

  // App.css sets `body { overflow: hidden }` for the dashboard shell, which has
  // its own scroll container; the landing page is a plain document, so let it
  // scroll while mounted.
  React.useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        در حال بارگذاری…
      </Box>
    );
  }

  // Authenticated visitors go straight to their dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const selected = FEATURES.find((f) => f.key === openFeature);

  return (
    <RTL>
      <Page>
        <NavBar>
          <Box component="img" src={logo} alt="برنا" sx={{ height: '2.5rem' }} />
          <NavLinks>
            {/* In-page scroll links hide on phones so the bar fits 390px; the hero repeats «ویژگی‌ها». */}
            <Button color="inherit" onClick={() => scrollTo('features')} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>ویژگی‌ها</Button>
            <Button component={Link} to="/login" variant="outlined" color="primary">ورود</Button>
            <Button component={Link} to="/signup" variant="contained" color="primary" disableElevation>
              ثبت‌نام آموزشگاه
            </Button>
          </NavLinks>
        </NavBar>

        <Hero>
          <Typography variant="h1" sx={{ mb: 2 }}>
            نرم‌افزار آنلاین مدیریت آموزشگاه
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            کارت رو کم کن، وقتت رو ذخیره کن
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/signup"
              variant="contained"
              size="large"
              sx={{ bgcolor: 'primary.contrastText', color: 'primary.main', '&:hover': { bgcolor: 'primary.soft' } }}
            >
              شروع کنید
            </Button>
            <Button
              size="large"
              onClick={() => scrollTo('features')}
              sx={{ color: 'primary.contrastText', border: (t) => `1px solid ${alpha(t.palette.primary.contrastText, 0.6)}` }}
            >
              ویژگی‌ها
            </Button>
          </Box>
        </Hero>

        <Section id="features">
          <Typography variant="h2" sx={{ textAlign: 'center', mb: 4 }}>
            ویژگی‌ها
          </Typography>
          <CardsGrid>
            {FEATURES.map((f) => (
              <FeatureCard
                key={f.key}
                active={openFeature === f.key}
                onClick={() => setOpenFeature(openFeature === f.key ? null : f.key)}
              >
                <f.icon color="primary" sx={{ fontSize: '3rem', mb: 1 }} />
                <Typography sx={{ fontWeight: 600 }}>{f.title}</Typography>
              </FeatureCard>
            ))}
          </CardsGrid>
          <Collapse in={Boolean(selected)}>
            {selected && (
              <FeatureText>
                <Typography variant="h6" sx={{ mb: 1 }}>{selected.title}</Typography>
                <Typography>{selected.text}</Typography>
              </FeatureText>
            )}
          </Collapse>

          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 700 }}>
              سایر ویژگی‌ها:
            </Typography>
            {OTHER_FEATURES.map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleOutlineIcon color="primary" fontSize="small" />
                <Typography>{item}</Typography>
              </Box>
            ))}
          </Box>
        </Section>

        <Footer>
          <Typography variant="body2" color="text.secondary">
            برنا — نرم‌افزار آنلاین مدیریت آموزشگاه
          </Typography>
        </Footer>
      </Page>
    </RTL>
  );
}
